/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaConstMapModel,
	isSchemaConstMapModel,
	TGenericBlueprintSchema
} from "@adapptio/lib-blueprint";
import { INSPECTOR_NODE_TYPE, TInspectorNode } from "../InspectorNodeTypes";
import { mapModelToNodes } from "../index";
import { isFunction } from "@adapptio/lib-shared";
import { mapperDescriptor } from "../../modules/Core/ReferenceGuide/mappers/mapperDescriptor";
import { mapperExamples } from "../../modules/Core/ReferenceGuide/mappers/mapperExamples";
import { getParentComponentName } from "../utils";

export function constMapMapper(model: ISchemaConstMapModel<TGenericBlueprintSchema>): TInspectorNode {
	if (!isSchemaConstMapModel(model)) {
		return;
	}

	const itemsDeps = model.items?.flatMap((modelEntry) => [
		...(mapModelToNodes(modelEntry.key)?.deps || []),
		...(mapModelToNodes(modelEntry.value)?.deps || [])
	]);

	return {
		type: INSPECTOR_NODE_TYPE.MAP,
		key: `${model.nodeId}`,
		label: model.schema.opts.label,
		tooltip: model.schema.opts.description,
		default: model.schema.opts.default || null,
		required: model.schema.opts.constraints?.required || false,
		options: model.schema.opts["options"],
		referenceData: getParentComponentName(model),
		descriptor: mapperDescriptor(model.schema),
		examples: mapperExamples(model.schema),
		deps: [ model, ...itemsDeps ],
		model: model,
		controls: [],
		getChildren: (mapEntry, index) => () => {
			const keyNode = mapModelToNodes(mapEntry.key);
			const valueNode = mapModelToNodes(mapEntry.value);

			const deps = [];

			const label =
				(isFunction(model.schema.getElementModelNodeInfo) &&
					model.schema.getElementModelNodeInfo(model, index)?.label) ||
				mapEntry.key.value;

			if (keyNode) {
				deps.push(...keyNode.deps);
			}

			if (valueNode) {
				deps.push(...valueNode.deps);
			}

			return {
				type: INSPECTOR_NODE_TYPE.DEFAULT,
				key: String(mapEntry.key.nodeId),
				label: label,
				tooltip: "",
				deps: [ mapEntry.key, mapEntry.value, ...deps ],
				controls: [],
				children: [ () => mapModelToNodes(mapEntry.key), () => mapModelToNodes(mapEntry.value) ]
			};
		}
	};
}
